import { useState } from 'react';
import { Button, Checkbox, Table, Typography, theme } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { PlusOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CustomerContactResponse } from '@/types/api/customer';
import TableContainer from '@/components/common/TableContainer';
import { controlHeightTokens } from '@/styles/tokens';
import { formatPhoneFromApiForDisplay, formatMobileFromApiForDisplay } from '@/utils/phoneUtils';
import { ContactAddressDisplay } from '@/components/common/ContactAddressDisplay';

const { Text } = Typography;

/**
 * #91 — the customer detail contact section is VIEW-ONLY.
 * Contacts are created and edited exclusively in the Contacts module;
 * each row links to that contact's page there (`/auth/contacts/:id`).
 */
interface ContactsSectionProps {
  contacts: CustomerContactResponse[] | undefined;
  includeInactive: boolean;
  onIncludeInactiveChange: (checked: boolean) => void;
  /** Navigate to the contact's page in the Contacts module. */
  onOpenContact: (contact: CustomerContactResponse) => void;
  /** Navigate to the Contacts module (create surface lives there). */
  onAddInContactsModule: () => void;
  loading: boolean;
}

const ContactsSection = ({
  contacts,
  includeInactive,
  onIncludeInactiveChange,
  onOpenContact,
  onAddInContactsModule,
  loading,
}: ContactsSectionProps) => {
  const { t } = useTranslation('customers');
  const { token } = theme.useToken();

  // Track if user has applied any sorting
  const [sortedInfo, setSortedInfo] = useState<SorterResult<CustomerContactResponse>>({});

  return (
    <div>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('customer.sections.contacts')}
        </h3>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => onIncludeInactiveChange(e.target.checked)}
          >
            {t('customer.labels.includeInactiveContacts')}
          </Checkbox>
          {/* #91 方式2 — the add button LINKS to the Contacts module */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
            onClick={onAddInContactsModule}
          >
            {t('customer.contactsPolicy.addContact')}
          </Button>
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('customer.contactsPolicy.managedInModule')}
      </Text>
      <TableContainer>
        <Table<CustomerContactResponse>
          dataSource={
            contacts
              ?.filter((c) => includeInactive || c.isActive)
              ?.slice()
              ?.sort((a, b) => {
                // Only apply default sort when no user sorting is active
                if (!sortedInfo.order) {
                  // Default sort: Primary first, then by contactId descending (newest first)
                  if (a.isPrimary !== b.isPrimary) {
                    return a.isPrimary ? -1 : 1;
                  }
                  return b.contactId - a.contactId;
                }
                // Let Ant Design handle the sorting when user has selected a column
                return 0;
              }) || []
          }
          rowKey="contactId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          onRow={(record) => ({
            onClick: () => onOpenContact(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          onChange={(_pagination, _filters, sorter) => {
            setSortedInfo(Array.isArray(sorter) ? sorter[0] : sorter);
          }}
          columns={[
            {
              title: t('customer.table.primary'),
              dataIndex: 'isPrimary',
              key: 'isPrimary',
              width: 120,
              align: 'center',
              showSorterTooltip: false,
              render: (isPrimary: boolean) =>
                isPrimary ? (
                  <CheckOutlined style={{ color: token.colorPrimary, fontSize: token.sizeMD }} />
                ) : null,
            },
            {
              title: t('customer.table.name'),
              dataIndex: 'name',
              key: 'name',
              sorter: (a, b) => a.name.localeCompare(b.name),
              sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
              showSorterTooltip: false,
              // #91 — clickable name links to the contact's page in the Contacts module
              render: (name: string, record: CustomerContactResponse) => (
                <a
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenContact(record);
                  }}
                  style={{ color: token.colorPrimary, cursor: 'pointer' }}
                >
                  {name}
                </a>
              ),
            },
            {
              title: t('customer.table.jobTitle'),
              dataIndex: 'jobTitle',
              key: 'jobTitle',
              sorter: (a, b) => (a.jobTitle || '').localeCompare(b.jobTitle || ''),
              sortOrder: sortedInfo.columnKey === 'jobTitle' ? sortedInfo.order : null,
              showSorterTooltip: false,
              render: (title: string | null) => title || '-',
            },
            {
              title: t('customer.table.phone'),
              key: 'phone',
              sorter: (a, b) => (a.phoneNumber || '').localeCompare(b.phoneNumber || ''),
              sortOrder: sortedInfo.columnKey === 'phone' ? sortedInfo.order : null,
              showSorterTooltip: false,
              render: (_: unknown, record: CustomerContactResponse) => {
                return (
                  formatPhoneFromApiForDisplay(
                    {
                      phoneCountryCode: record.phoneCountryCode,
                      phoneNumber: record.phoneNumber,
                      phoneExtension: record.phoneExtension,
                    },
                    t('customer.fields.ext')
                  ) || '-'
                );
              },
            },
            {
              title: t('customer.table.mobile'),
              key: 'mobile',
              sorter: (a, b) => (a.mobileNumber || '').localeCompare(b.mobileNumber || ''),
              sortOrder: sortedInfo.columnKey === 'mobile' ? sortedInfo.order : null,
              showSorterTooltip: false,
              render: (_: unknown, record: CustomerContactResponse) => {
                return (
                  formatMobileFromApiForDisplay({
                    mobileCountryCode: record.mobileCountryCode,
                    mobileNumber: record.mobileNumber,
                  }) || '-'
                );
              },
            },
            {
              title: t('customer.table.fax'),
              dataIndex: 'fax',
              key: 'fax',
              sorter: (a, b) => (a.fax || '').localeCompare(b.fax || ''),
              sortOrder: sortedInfo.columnKey === 'fax' ? sortedInfo.order : null,
              showSorterTooltip: false,
              render: (text: string | null) => text || '-',
            },
            {
              title: t('customer.table.email'),
              dataIndex: 'email',
              key: 'email',
              sorter: (a, b) => a.email.localeCompare(b.email),
              sortOrder: sortedInfo.columnKey === 'email' ? sortedInfo.order : null,
              showSorterTooltip: false,
              render: (email: string) => <a href={`mailto:${email}`}>{email}</a>,
            },
            {
              title: t('customer.table.address'),
              key: 'address',
              width: 300,
              ellipsis: true,
              sorter: (a, b) => (a.address || '').localeCompare(b.address || ''),
              sortOrder: sortedInfo.columnKey === 'address' ? sortedInfo.order : null,
              showSorterTooltip: false,
              render: (_: unknown, record: CustomerContactResponse) => (
                <ContactAddressDisplay contact={record} />
              ),
            },
          ]}
        />
      </TableContainer>
    </div>
  );
};

export default ContactsSection;
